import apiClient from './apiClient';
import { researchErrorMessage, type ResearchMetadata, type StagingLaneVariant } from './risResearchStream';
import { normalizeChatAttachments, type ChatAttachmentSummary } from './chatAttachments';
import { istRechercheSperre } from '../utils/errorHandler';
import type { ResearchRunEnd } from './researchRunStore';
import { datumZeit } from '../utils/datum';

const ENDED_RUN_STATES: ReadonlySet<string> = new Set(['failed', 'cancelled', 'interrupted']);

/** Bahn und Variante eines Laufs, sofern der Server sie nennt: sie wählen den Auswerter der Oberfläche. */
function runLane(lane: unknown, variant: unknown): { lane?: 'A' | 'B'; variant?: StagingLaneVariant | null } {
  if (lane !== 'A' && lane !== 'B') return {};
  return { lane, variant: variant === 'B' || variant === 'C' || variant === 'D' ? variant : null };
}

/** Die Endzustände der Fragen ohne Antwort; Unbekanntes fällt weg. */
function mapEndedRuns(runs: unknown[]): Array<ResearchRunEnd & { questionMessageId: string }> {
  return runs.flatMap((run) => {
    if (!run || typeof run !== 'object') return [];
    const { id, questionMessageId, state, failureCode, failureMessage, lane, variant } = run as Record<string, unknown>;
    if (typeof id !== 'string' || typeof questionMessageId !== 'string' || typeof state !== 'string' || !ENDED_RUN_STATES.has(state)) return [];
    // Der Satz zum Fehlschlag, wie ihn der Lauf live meldete (researchRunStore, run.failed).
    const message = typeof failureCode === 'string' ? researchErrorMessage(failureCode, failureMessage, '') : '';
    return [{
      runId: id,
      questionMessageId,
      state: state as ResearchRunEnd['state'],
      failureCode: typeof failureCode === 'string' ? failureCode : null,
      ...(message ? { failureMessage: message } : {}),
      ...runLane(lane, variant),
    }];
  });
}

/** Der aktive Lauf eines Chats; Unbekanntes fällt weg, die Frage nennt nur die Chatliste. */
function mapActiveRun(run: unknown): EnhancedChatSession['activeRun'] {
  if (!run || typeof run !== 'object') return null;
  const { id, state, kind, lane, variant, questionMessageId, question } = run as Record<string, unknown>;
  if (typeof id !== 'string') return null;
  return {
    id,
    state: String(state),
    kind: String(kind),
    ...runLane(lane, variant),
    ...(typeof questionMessageId === 'string' ? { questionMessageId } : {}),
    ...(typeof question === 'string' ? { question } : {}),
  };
}

/** Die Markierung „neu“ eines Chats: sein letzter endgültiger, ungesehener Lauf. */
function mapUnseenRun(run: unknown): EnhancedChatSession['unseenRun'] {
  if (!run || typeof run !== 'object') return null;
  const { id, state, failureCode } = run as Record<string, unknown>;
  if (typeof id !== 'string' || typeof state !== 'string') return null;
  return { id, state, failureCode: typeof failureCode === 'string' ? failureCode : null };
}

// Enhanced Source interface for compatibility with main app
export interface EnhancedSource {
  type: 'paragraph' | 'judgment' | 'document';
  reference: string;
  title: string;
  risLink?: string;
  found: boolean;
  documentId?: string;
  confidence?: number;
  /**
   * Quellenart und Datumsangaben der Bahn-B-Recherche. Der Quellen-Fußbereich
   * des Gesprächs gruppiert danach und beschriftet die Zeile damit; ohne diese
   * Felder bliebe von der gelieferten Quelle nur ihre Adresse übrig.
   */
  kind?: string;
  decisionDate?: string;
  validFrom?: string;
}

export type ResearchSourceProvenance = 'retrieved' | 'declared' | 'verified';

export interface ResearchSourceRecord {
  provenanceType?: ResearchSourceProvenance;
  reference?: string;
  title?: string;
  url?: string;
  urls?: string[];
  risLink?: string;
  documentId?: string;
  dokumentId?: string;
  technicalId?: string;
  id?: string;
  type?: string;
  [key: string]: unknown;
}

export type ResearchSource = string | ResearchSourceRecord;

export function normalizeResearchSources(value: unknown): ResearchSource[] {
  if (!Array.isArray(value)) return [];
  return value.filter((source): source is ResearchSource =>
    typeof source === 'string' || (typeof source === 'object' && source !== null && !Array.isArray(source))
  );
}

function firstString(...values: unknown[]): string | undefined {
  return values.find((value): value is string => typeof value === 'string' && value.trim().length > 0);
}

function toEnhancedVerifiedSource(value: unknown): EnhancedSource | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value as ResearchSourceRecord;
  const documentId = firstString(source.documentId, source.dokumentId);
  const reference = firstString(source.reference, source.title, source.technicalId, documentId, source.id);
  if (!reference) return null;
  const url = firstString(source.risLink, source.url, ...(Array.isArray(source.urls) ? source.urls : []));
  const type = source.type === 'paragraph' || source.type === 'judgment' || source.type === 'document'
    ? source.type
    : documentId
      ? 'document'
      : 'judgment';
  const normalized: EnhancedSource = {
    type,
    reference,
    title: firstString(source.title, reference)!,
    found: typeof source.found === 'boolean'
      ? source.found
      : typeof source.verified === 'boolean'
        ? source.verified
        : true,
  };
  if (url) normalized.risLink = url;
  if (documentId) normalized.documentId = documentId;
  if (typeof source.confidence === 'number') normalized.confidence = source.confidence;
  const kind = firstString(source.kind);
  if (kind) normalized.kind = kind;
  const decisionDate = firstString(source.decisionDate);
  if (decisionDate) normalized.decisionDate = decisionDate;
  const validFrom = firstString(source.validFrom);
  if (validFrom) normalized.validFrom = validFrom;
  return normalized;
}

export function normalizeVerifiedSources(value: unknown): EnhancedSource[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((source) => {
    const normalized = toEnhancedVerifiedSource(source);
    return normalized ? [normalized] : [];
  });
}

export function verifiedSourcesFromProvenance(sources: ResearchSource[]): EnhancedSource[] {
  return sources.flatMap((source) => {
    if (typeof source === 'string' || source.provenanceType !== 'verified') return [];
    const normalized = toEnhancedVerifiedSource(source);
    return normalized ? [normalized] : [];
  });
}

// Enhanced Chat Session interface matching main app
export interface EnhancedChatSession {
  id: number;
  sessionId: string;
  title: string;
  serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent';
  createdAt: Date;
  updatedAt: Date;
  lastMessageAt: Date;
  messageCount: number;
  isActive: boolean;
  previewText?: string;
  /**
   * Der laufende oder wartende Lauf dieses Chats; die Oberfläche nimmt ihn beim Laden der App und beim Öffnen wieder auf.
   * Frage-ID und Text der Frage nennt die Chatliste.
   */
  activeRun?: {
    id: string; state: string; kind: string; lane?: 'A' | 'B'; variant?: StagingLaneVariant | null; questionMessageId?: string; question?: string;
  } | null;
  /** Die Markierung „neu“: der letzte endgültige Lauf, solange ihn niemand gesehen hat. */
  unseenRun?: { id: string; state: string; failureCode: string | null } | null;
  /** Nur am einzelnen Chat: die Fragen ohne Antwort mit dem Endzustand ihres letzten Laufs (Status und „Wiederholen“). */
  endedRuns?: Array<ResearchRunEnd & { questionMessageId: string }>;
  chatType?: 'simple' | 'advanced' | 'multi-agent' | 'ris';
  fileInfo?: {
    originalName: string;
    geminiFileUri?: string;
    extractedTextLength?: number;
  };
}

// Enhanced Chat Message interface matching main app
export interface EnhancedChatMessage {
  id: number;
  sessionId: string;
  role: 'user' | 'model';
  content: string;
  timestamp: Date;
  metadata?: {
    agentName?: string;
    contentLength?: number;
    fileReference?: string;
    sources?: ResearchSource[];
    verifiedSources?: EnhancedSource[];
    sourceVerificationStatus?: 'pending' | 'completed' | 'failed';
    messageId?: string;
    serviceType?: 'ris' | 'berufung' | 'multiagent';
    messageIndex?: number;
    risLinkMetadata?: any;
    researchMetadata?: ResearchMetadata;
    /** Die Anhänge, die mit dieser Frage gegangen sind. */
    attachments?: ChatAttachmentSummary[];
    researchId?: string;
    rating?: { verdict: 'UP' | 'DOWN'; category: string | null } | null;
  };
}

// Legacy interfaces for backward compatibility
export interface ChatSession {
  id: string;
  sessionId: string;
  serviceType: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  isActive: boolean;
}

export interface ChatMessage {
  id: string;
  messageId: string;
  role: 'user' | 'assistant';
  content: string;
  mode?: string;
  sources?: any[];
  verifiedSources?: any;
  researchMetadata?: ResearchMetadata;
  qualityScore?: number;
  createdAt: string;
}

export interface ChatSessionWithMessages extends ChatSession {
  messages: ChatMessage[];
}

export const SERVICE_TYPE_CONFIG = {
  ris: {
    label: 'Recherche',
    icon: '🔍',
    description: 'KI-gestützte Rechtsrecherche',
    supportsSourceVerification: true,
    maxSessions: 50
  },
  clairis: {
    label: 'Chat',
    icon: '💬',
    description: 'Rechtlicher KI-Assistent',
    supportsSourceVerification: false,
    maxSessions: 50
  },
  berufung: {
    label: 'Berufungsassistent',
    icon: '📝',
    description: 'Berufungsschreiben Generator',
    supportsSourceVerification: false,
    maxSessions: 30
  },
  multiagent: {
    label: 'Multi-Agent System',
    icon: '🤖',
    description: 'Kollaborative KI-Agenten',
    supportsSourceVerification: false,
    maxSessions: 20
  }
};

class WebChatHistoryService {
  // Helper to convert API response to EnhancedChatSession
  private mapToEnhancedSession(session: any): EnhancedChatSession {
    return {
      id: typeof session.id === 'string' ? parseInt(session.id, 10) : session.id,
      sessionId: session.sessionId,
      title: session.title || 'Unbenannte Recherche',
      serviceType: (session.serviceType || 'ris') as 'ris' | 'berufung' | 'multiagent',
      createdAt: new Date(session.createdAt),
      updatedAt: new Date(session.updatedAt),
      lastMessageAt: new Date(session.lastMessageAt || session.updatedAt),
      messageCount: session.messageCount || 0,
      isActive: session.isActive !== false,
      previewText: session.previewText,
      activeRun: mapActiveRun(session.activeRun),
      unseenRun: mapUnseenRun(session.unseenRun),
      ...(Array.isArray(session.endedRuns) ? { endedRuns: mapEndedRuns(session.endedRuns) } : {}),
    };
  }

  // Helper to convert API response to EnhancedChatMessage
  private mapToEnhancedMessage(msg: any): EnhancedChatMessage {
    const sources = normalizeResearchSources(msg.sources);
    const explicitVerifiedSources = normalizeVerifiedSources(msg.verifiedSources);
    const verifiedSources = explicitVerifiedSources.length > 0
      ? explicitVerifiedSources
      : verifiedSourcesFromProvenance(sources);
    const attachments = normalizeChatAttachments(msg.attachments);
    return {
      id: typeof msg.id === 'string' ? parseInt(msg.id, 10) : msg.id,
      sessionId: msg.sessionId,
      role: msg.role === 'assistant' ? 'model' : 'user',
      content: msg.content,
      timestamp: new Date(msg.createdAt || msg.timestamp),
      metadata: {
        sources: sources.length > 0 ? sources : undefined,
        verifiedSources: verifiedSources.length > 0 ? verifiedSources : undefined,
        sourceVerificationStatus: verifiedSources.length > 0 ? 'completed' : undefined,
        messageId: msg.messageId,
        serviceType: 'ris',
        researchMetadata: msg.researchMetadata,
        attachments: attachments.length > 0 ? attachments : undefined,
        researchId: msg.researchId ?? undefined,
        rating: msg.rating ?? null,
      }
    };
  }

  // Generate a session ID (client-side for compatibility)
  async generateSessionId(serviceType: string, userId?: number): Promise<string> {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const userPrefix = userId ? `${userId}_` : '';
    return `${userPrefix}${serviceType}_${timestamp}_${random}`;
  }

  // Get sessions by service type - returns EnhancedChatSession[]
  async getSessionsByServiceType(
    serviceType: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<EnhancedChatSession[]> {
    try {
      const response = await apiClient.get('/api/ris/sessions', {
        params: { serviceType, limit, offset },
      });

      const sessions = response.data.sessions || response.data || [];
      // D7: ein Chat ohne Nachricht (etwa nach einem abgewiesenen ersten Start oder mit nur hochgeladenen Anhängen) bleibt
      // gültig, steht aber nicht in der Liste; mit seiner ersten Frage (lastMessageAt, jeder Schreibweg setzt es) erscheint er.
      return sessions
        .filter((s: any) => s.lastMessageAt || s.activeRun)
        .map((s: any) => this.mapToEnhancedSession(s));
    } catch {
      console.error('[WebChatHistoryService] Session list fetch failed');
      return [];
    }
  }

  // Get a specific chat session with messages
  async getChatSession(sessionId: string): Promise<{ session: EnhancedChatSession; messages: EnhancedChatMessage[] }> {
    try {
      const response = await apiClient.get(`/api/ris/sessions/${sessionId}`);
      const data = response.data;

      const session = this.mapToEnhancedSession(data.session || data);
      const messages = (data.messages || []).map((m: any) => this.mapToEnhancedMessage(m));

      return { session, messages };
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Session not found');
      }
      throw error;
    }
  }

  // Create a new chat session (V2 API)
  async createChatSessionV2(data: {
    sessionId: string;
    title: string;
    serviceType: 'ris' | 'berufung' | 'multiagent';
    fileInfo?: {
      originalName: string;
      geminiFileUri?: string;
      extractedTextLength?: number;
    };
  }): Promise<EnhancedChatSession> {
    try {
      const response = await apiClient.post('/api/ris/sessions', {
        sessionId: data.sessionId,
        serviceType: data.serviceType,
        title: data.title
      });
      return this.mapToEnhancedSession(response.data.session || response.data);
    } catch (error) {
      // F13-6: eine Recherchesperre ist kein Fehler; weiterwerfen ohne Ausgabe, der Aufrufer bricht ab.
      if (!istRechercheSperre(error)) console.error('[WebChatHistoryService] Session creation failed');
      throw error;
    }
  }

  // Legacy create method
  async createChatSession(data: {
    sessionId: string;
    serviceType: string;
    title?: string;
  }): Promise<ChatSession> {
    const response = await apiClient.post('/api/ris/sessions', data);
    return response.data.session;
  }

  // Update session title
  async updateSessionTitle(sessionId: string, title: string): Promise<void> {
    await apiClient.patch(`/api/ris/sessions/${sessionId}`, { title });
  }

  // Deactivate (soft delete) a session
  async deactivateSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/ris/sessions/${sessionId}`);
  }

  /** Meldet einen endgültigen Lauf als gesehen; die Markierung „neu“ seines Chats entfällt auf jedem Gerät. */
  async markRunSeen(runId: string): Promise<void> {
    await apiClient.post(`/api/ris/runs/${encodeURIComponent(runId)}/seen`);
  }

  // Search chat sessions
  async searchChatSessions(
    query: string,
    limit: number = 20
  ): Promise<ChatSession[]> {
    try {
      const response = await apiClient.get('/api/ris/sessions/search', {
        params: { q: query, limit },
      });
      return response.data.sessions || [];
    } catch {
      console.error('[WebChatHistoryService] Session search failed');
      return [];
    }
  }

  // Get chat statistics
  async getChatStats(): Promise<{
    totalSessions: number;
    totalMessages: number;
    sessionsByType: Record<string, number>;
  }> {
    try {
      const response = await apiClient.get('/api/ris/sessions/stats');
      return response.data;
    } catch {
      console.error('[WebChatHistoryService] Chat statistics fetch failed');
      return { totalSessions: 0, totalMessages: 0, sessionsByType: {} };
    }
  }

  // Get total session count
  async getTotalSessionCount(serviceType?: string): Promise<number> {
    try {
      const stats = await this.getChatStats();
      if (serviceType) {
        return stats.sessionsByType[serviceType] || 0;
      }
      return stats.totalSessions;
    } catch (error) {
      return 0;
    }
  }

  // Static utility methods for formatting
  static formatDate(dateString: string): string {
    return datumZeit(dateString);
  }

  static formatPreview(content: string, maxLength: number = 100): string {
    if (!content) return '';

    const cleanContent = content
      .replace(/[#*`_~]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (cleanContent.length <= maxLength) {
      return cleanContent;
    }

    return cleanContent.substring(0, maxLength) + '...';
  }

  static getServiceTypeLabel(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return SERVICE_TYPE_CONFIG[serviceType]?.label || serviceType;
  }

  static getServiceTypeIcon(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return SERVICE_TYPE_CONFIG[serviceType]?.icon || '?';
  }

  // Instance methods for convenience
  formatDate(dateString: string): string {
    return WebChatHistoryService.formatDate(dateString);
  }

  formatPreview(content: string, maxLength: number = 100): string {
    return WebChatHistoryService.formatPreview(content, maxLength);
  }

  getServiceTypeLabel(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return WebChatHistoryService.getServiceTypeLabel(serviceType);
  }

  getServiceTypeIcon(serviceType: 'ris' | 'clairis' | 'berufung' | 'multiagent'): string {
    return WebChatHistoryService.getServiceTypeIcon(serviceType);
  }
}

export const webChatHistoryService = new WebChatHistoryService();
export default webChatHistoryService;
